// Client-side review history helpers. Talks to /api/history.
import { authHeader } from './api.js';

/**
 * SHA-256 hex of the extracted file text. Used as a stable identifier so
 * the same file reviewed twice doesn't create duplicate-looking rows.
 */
export async function sha256Hex(text) {
  const buf = new TextEncoder().encode(text || '');
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Save a completed review. Returns { id, url } from Notion or throws.
 * Caller should treat this as fire-and-forget so a Notion outage doesn't
 * break the pipeline UX — wrap in try/catch.
 */
export async function saveReview(payload) {
  const res = await fetch('/api/history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || body.error || detail;
    } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetch recent reviews newest first. Returns the array, or throws.
 */
export async function listReviews({ limit = 25 } = {}) {
  const url = `/api/history?limit=${encodeURIComponent(limit)}`;
  const res = await fetch(url, {
    headers: { ...authHeader() },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || body.error || detail;
    } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return data.reviews || [];
}

/**
 * Build the save payload from a completed pipeline result.
 */
export async function buildReviewPayload({ result, extracted }) {
  const facts = result?.facts || {};
  const scoring = result?.scoring || {};
  const usage = result?.usage || {};
  const fileHash = extracted?.text ? await sha256Hex(extracted.text) : '';
  return {
    customerName: facts?.customer?.name || extracted?.fileName || 'Untitled',
    score: typeof scoring.score === 'number' ? scoring.score : null,
    decision: scoring.decision || null,
    date: new Date().toISOString(),
    keyFailures: Array.isArray(scoring.topThreeFailures) ? scoring.topThreeFailures : [],
    reviewer: 'Shared Preview',
    fileHash,
    costUsd: typeof usage.costUsd === 'number' ? usage.costUsd : null,
    totalTokens: typeof usage.totalTokens === 'number' ? usage.totalTokens : null,
  };
}
